import {
  buildImpactPreview,
  getSpecSnapshot,
  runSpecification
} from "@automatry/spec-engine";
import {
  ImpactPreviewViewSchema,
  type ApartmentSolution,
  type ApartmentTakeoffRow,
  type CatalogProduct,
  type CatalogRelease,
  type ImpactPreview,
  type ImpactPreviewChange,
  type ImpactPreviewView,
  type OverrideDraft,
  type OverrideStructuredChange,
  type PointVector,
  type Requirement,
  type RuleSet,
  type SourceRef,
  type SpecSnapshot,
  type ValidationFinding
} from "@automatry/schemas";

type CatalogReleaseWithProducts = CatalogRelease & {
  products: CatalogProduct[];
};

export interface CarringtonEngineFixture {
  fixture: {
    id: string;
    generatedFrom: string;
    sourceInput: string;
    generatedAt: string;
  };
  catalog: CatalogReleaseWithProducts;
  rules: RuleSet;
  engineInput: {
    projectId: string;
    takeoffRows: ApartmentTakeoffRow[];
    requirements: Requirement[];
  };
  baselines: {
    apartmentTypeId: string;
    runId: string;
    snapshotId: string;
    solutionHash: string;
    status: string;
    validationStatus: string;
    sourceOverrideIds: string[];
    productQuantities: Record<string, number>;
    pointTotals: PointVector;
    riskCount: number;
  }[];
}

export interface ScopedOverridePreviewRequest {
  fixture: CarringtonEngineFixture;
  apartmentTypeId: string;
  actionId: string;
  label: string;
  intent: string;
  reason: string;
  structuredChange: OverrideStructuredChange;
  kind?: OverrideDraft["kind"];
  scopeRefId?: string;
}

export interface ImpactProductDelta {
  productRef: string;
  label: string;
  before: number;
  after: number;
  delta: number;
  priceKnown: boolean;
  unitCost?: ImpactMoneyDelta;
  costDelta?: ImpactMoneyDelta;
}

export interface ImpactMoneyDelta {
  amount: number;
  currency: "GBP" | "EUR" | "USD";
  formatted: string;
  source: string;
}

export interface ImpactCapacityDelta {
  pointKind: PointKey;
  requiredBefore: number;
  requiredAfter: number;
  availableBefore: number;
  availableAfter: number;
  spareBefore: number;
  spareAfter: number;
  violationCountBefore: number;
  violationCountAfter: number;
}

export interface ImpactRiskDelta {
  before: number;
  after: number;
  delta: number;
  newlyRaisedRiskIds: string[];
  clearedRiskIds: string[];
}

export interface ScopedOverridePreviewResult {
  actionId: string;
  label: string;
  overrideDraft: OverrideDraft;
  enginePreview: ImpactPreview;
  view: ImpactPreviewView;
  baseRunId: string;
  overrideRunId: string;
  baseStatus: string;
  overrideStatus: string;
  baseSnapshot: SpecSnapshot;
  overrideSnapshot: SpecSnapshot;
  productDeltas: ImpactProductDelta[];
  costDelta: ImpactMoneyDelta;
  capacityDeltas: ImpactCapacityDelta[];
  riskDelta: ImpactRiskDelta;
  validationFindings: ValidationFinding[];
  baseUnchanged: boolean;
}

export interface LiveRequirementDeriveRequest {
  fixture: CarringtonEngineFixture;
  apartmentTypeId: string;
  requirements: Requirement[];
}

export interface LiveRequirementDeriveResult {
  runId: string;
  snapshotId: string;
  solutionHash: string;
  status: string;
  validationStatus: string;
  requirementCount: number;
  sourceRequirementIds: string[];
  snapshot: SpecSnapshot;
}

const POINT_KEYS = ["ui", "di", "ao", "do", "relay"] as const;
type PointKey = (typeof POINT_KEYS)[number];

const sourceRefForOverride = (input: {
  apartmentTypeId: string;
  actionId: string;
}): SourceRef => ({
  sourceId: `ui-7-${input.apartmentTypeId}-${input.actionId}`,
  sourceKind: "manual_entry",
  authorityClass: "baseline_note",
  locator: `apps/web/src/impactPreview.ts:${input.apartmentTypeId}:${input.actionId}`
});

const safeId = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);

const money = (
  amount: number,
  currency: ImpactMoneyDelta["currency"],
  source: string
): ImpactMoneyDelta => ({
  amount: Number(amount.toFixed(2)),
  currency,
  formatted: new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency
  }).format(amount),
  source
});

const productFor = (
  catalog: CatalogReleaseWithProducts,
  productRef: string
): CatalogProduct | undefined =>
  catalog.products.find(
    (product) =>
      product.partNo === productRef || product.aliases.includes(productRef)
  );

const numericChange = (
  change: ImpactPreviewChange
): change is ImpactPreviewChange & {
  before: number;
  after: number;
  delta: number;
} =>
  change.kind === "product_quantity" &&
  typeof change.before === "number" &&
  typeof change.after === "number" &&
  typeof change.delta === "number";

const productDeltasFromPreview = (
  preview: ImpactPreview,
  catalog: CatalogReleaseWithProducts
): ImpactProductDelta[] =>
  (preview.changes ?? []).filter(numericChange).map((change) => {
    const product = productFor(catalog, change.subjectRef);
    const unitPrice = product?.price;
    const base = {
      productRef: change.subjectRef,
      label: product?.description ?? change.subjectRef,
      before: change.before,
      after: change.after,
      delta: change.delta,
      priceKnown: unitPrice !== undefined
    };

    if (!unitPrice) {
      return base;
    }

    return {
      ...base,
      unitCost: money(unitPrice.amount, unitPrice.currency, "products price list"),
      costDelta: money(
        change.delta * unitPrice.amount,
        unitPrice.currency,
        "engine quantity delta x products price list"
      )
    };
  });

const totalCostDelta = (deltas: readonly ImpactProductDelta[]): ImpactMoneyDelta => {
  const currency = deltas.find((delta) => delta.costDelta)?.costDelta?.currency ?? "GBP";
  const amount = deltas.reduce(
    (sum, delta) => sum + (delta.costDelta?.amount ?? 0),
    0
  );

  return money(amount, currency, "engine quantity deltas x products price list");
};

const firstSolution = (snapshot: SpecSnapshot): ApartmentSolution => {
  const solution = snapshot.apartmentSolutions[0];

  if (!solution) {
    throw new Error(`Snapshot ${snapshot.id} has no apartment solution.`);
  }

  return solution;
};

const violationCountFor = (
  solution: ApartmentSolution,
  pointKind: PointKey
): number => {
  const violations: readonly { pointKind: PointKey }[] =
    solution.pointLedger.violations;

  return violations.filter(
    (violation) => violation.pointKind === pointKind
  ).length;
};

const capacityDeltasFromSnapshots = (
  before: SpecSnapshot,
  after: SpecSnapshot
): ImpactCapacityDelta[] => {
  const beforeSolution = firstSolution(before);
  const afterSolution = firstSolution(after);

  return POINT_KEYS.flatMap((pointKind) => {
    const row: ImpactCapacityDelta = {
      pointKind,
      requiredBefore: beforeSolution.pointLedger.required[pointKind],
      requiredAfter: afterSolution.pointLedger.required[pointKind],
      availableBefore: beforeSolution.pointLedger.available[pointKind],
      availableAfter: afterSolution.pointLedger.available[pointKind],
      spareBefore: beforeSolution.pointLedger.spare[pointKind],
      spareAfter: afterSolution.pointLedger.spare[pointKind],
      violationCountBefore: violationCountFor(beforeSolution, pointKind),
      violationCountAfter: violationCountFor(afterSolution, pointKind)
    };

    return row.requiredBefore !== row.requiredAfter ||
      row.availableBefore !== row.availableAfter ||
      row.spareBefore !== row.spareAfter ||
      row.violationCountBefore !== row.violationCountAfter
      ? [row]
      : [];
  });
};

const riskDeltaFromSnapshots = (
  before: SpecSnapshot,
  after: SpecSnapshot
): ImpactRiskDelta => {
  const beforeRisks: readonly { id: string }[] = firstSolution(before).risks;
  const afterRisks: readonly { id: string }[] = firstSolution(after).risks;
  const beforeRiskIds = new Set<string>(beforeRisks.map((risk) => risk.id));
  const afterRiskIds = new Set<string>(afterRisks.map((risk) => risk.id));
  const newlyRaisedRiskIds = [...afterRiskIds].filter((id) => !beforeRiskIds.has(id));
  const clearedRiskIds = [...beforeRiskIds].filter((id) => !afterRiskIds.has(id));

  return {
    before: beforeRiskIds.size,
    after: afterRiskIds.size,
    delta: afterRiskIds.size - beforeRiskIds.size,
    newlyRaisedRiskIds,
    clearedRiskIds
  };
};

const guardrailFor = (input: {
  preview: ImpactPreview;
  after: SpecSnapshot;
}): NonNullable<ImpactPreviewView["guardrail"]> => {
  const findings: readonly ValidationFinding[] = input.after.validation.findings;
  const findingCodes = findings.map((finding) => finding.code);
  const firstFinding = findings[0];

  if (input.preview.validationStatus === "fail") {
    return {
      status: "blocked",
      message:
        firstFinding?.message ??
        "The engine rejected this scoped override during validation.",
      findingCodes
    };
  }

  if (input.preview.validationStatus === "warning") {
    return {
      status: "warning",
      message:
        firstFinding?.message ??
        "The engine accepted this scoped override with review findings.",
      findingCodes
    };
  }

  return {
    status: "passed",
    message: "The engine accepted this scoped override.",
    findingCodes
  };
};

const makeOverrideDraft = (
  request: ScopedOverridePreviewRequest
): OverrideDraft => {
  const sourceRef = sourceRefForOverride({
    apartmentTypeId: request.apartmentTypeId,
    actionId: request.actionId
  });

  return {
    id: `override-ui7-${safeId(request.apartmentTypeId)}-${safeId(
      request.actionId
    )}`,
    projectId: request.fixture.engineInput.projectId,
    scope: {
      level: "apartment_type",
      refId: request.scopeRefId ?? request.apartmentTypeId
    },
    kind: request.kind ?? "human_override",
    intent: request.intent,
    structuredChange: request.structuredChange,
    priority: "normal",
    sourceRefs: [sourceRef],
    reason: request.reason,
    status: "ready",
    createdAt: request.fixture.fixture.generatedAt,
    createdBy: "ui-impact-preview"
  };
};

export const buildScopedOverridePreview = (
  request: ScopedOverridePreviewRequest
): ScopedOverridePreviewResult => {
  const overrideDraft = makeOverrideDraft(request);
  const engineInput = {
    ...request.fixture.engineInput,
    apartmentTypeId: request.apartmentTypeId
  };
  const deps = {
    catalog: request.fixture.catalog,
    rules: request.fixture.rules
  };
  const baseRun = runSpecification(engineInput, deps);
  const baseSnapshot = getSpecSnapshot(baseRun);
  const baseSnapshotBeforePreview = structuredClone(baseSnapshot);
  const overrideRun = runSpecification(
    {
      ...engineInput,
      overrides: [overrideDraft]
    },
    deps
  );
  const overrideSnapshot = getSpecSnapshot(overrideRun);
  const enginePreview = buildImpactPreview({
    projectId: request.fixture.engineInput.projectId,
    overrideDraftId: overrideDraft.id,
    before: baseSnapshot,
    after: overrideSnapshot,
    createdAt: request.fixture.fixture.generatedAt
  });
  const productDeltas = productDeltasFromPreview(
    enginePreview,
    request.fixture.catalog
  );
  const costDelta = totalCostDelta(productDeltas);
  const capacityDeltas = capacityDeltasFromSnapshots(baseSnapshot, overrideSnapshot);
  const riskDelta = riskDeltaFromSnapshots(baseSnapshot, overrideSnapshot);
  const guardrail = guardrailFor({
    preview: enginePreview,
    after: overrideSnapshot
  });
  const view = ImpactPreviewViewSchema.parse({
    projectId: enginePreview.projectId,
    impactPreviewId: enginePreview.id,
    status: enginePreview.validationStatus === "fail" ? "blocked" : "ready",
    affectedRequirementIds: enginePreview.affectedRequirementIds,
    affectedApartmentTypeIds: enginePreview.affectedApartmentTypeIds,
    affectedRiskIds: enginePreview.affectedRiskIds,
    summary: enginePreview.summary,
    updatedAt: enginePreview.createdAt,
    overrideDraftId: overrideDraft.id,
    baseSnapshotId: baseSnapshot.id,
    previewSnapshotId: overrideSnapshot.id,
    validationStatus: enginePreview.validationStatus,
    changeRows: enginePreview.changes ?? [],
    productQuantityDeltas: productDeltas,
    costDelta,
    capacityDeltas,
    riskDelta,
    guardrail
  });

  return {
    actionId: request.actionId,
    label: request.label,
    overrideDraft,
    enginePreview,
    view,
    baseRunId: baseRun.id,
    overrideRunId: overrideRun.id,
    baseStatus: baseRun.status,
    overrideStatus: overrideRun.status,
    baseSnapshot,
    overrideSnapshot,
    productDeltas,
    costDelta,
    capacityDeltas,
    riskDelta,
    validationFindings: overrideSnapshot.validation.findings,
    baseUnchanged:
      JSON.stringify(baseSnapshotBeforePreview) === JSON.stringify(baseSnapshot)
  };
};

export const deriveLiveRequirements = (
  request: LiveRequirementDeriveRequest
): LiveRequirementDeriveResult => {
  const takeoffRows = request.fixture.engineInput.takeoffRows.filter(
    (row) => row.apartmentTypeId === request.apartmentTypeId
  );

  if (takeoffRows.length === 0) {
    throw new Error(
      `No take-off rows are available for apartment type ${request.apartmentTypeId}.`
    );
  }

  const approvedRequirements = request.requirements.filter(
    (requirement) => requirement.status === "approved"
  );

  if (approvedRequirements.length === 0) {
    throw new Error(
      "Lane-A returned no approved canonical requirements for the generated preview."
    );
  }

  const run = runSpecification(
    {
      projectId: request.fixture.engineInput.projectId,
      apartmentTypeId: request.apartmentTypeId,
      takeoffRows,
      requirements: approvedRequirements
    },
    {
      catalog: request.fixture.catalog,
      rules: request.fixture.rules
    }
  );
  const snapshot = getSpecSnapshot(run);

  return {
    runId: run.id,
    snapshotId: snapshot.id,
    solutionHash: run.solutionHash,
    status: run.status,
    validationStatus: snapshot.validation.status,
    requirementCount: approvedRequirements.length,
    sourceRequirementIds: approvedRequirements.map((requirement) => requirement.id),
    snapshot
  };
};
